import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUX_PLUS_TOURS_CAMPAIGN } from 'luxPlus/constants/base'
import { dateIsBefore, dateIsAfterOrSame } from 'lib/datetime/dateUtils'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'
import useSwitchRegion from 'hooks/useSwitchRegion'

const BANNER_ID = 'luxPlusToursOfferBanner'

function useLuxPlusToursOfferBanner() {
  const isHidden = useIsBannerHidden('luxPlusToursOfferBanner')
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const currentRegionCodeLower = currentRegionCode.toLowerCase()
  const isCurrentRegionAustralia = currentRegionCodeLower === 'au'
  const memberSubscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const disableMembership = useAppSelector(state => state.auth.account.luxPlus.member.disableMembership)

  const canRedeemLuxPlusBenefitsAnyRegion =
    isLuxPlusEnabled &&
    memberSubscriptionItem?.status === 'ACTIVE' &&
    memberSubscriptionItem?.tier !== 'free_preview' &&
    !disableMembership

  const memberRegion = memberSubscriptionItem?.region?.toLowerCase()
  const isMember = canRedeemLuxPlusBenefitsAnyRegion && !!memberRegion
  const isMembershipInToursRegion = isMember && LUX_PLUS_TOURS_CAMPAIGN.TOUR_REGIONS.has(memberRegion)
  const isCurrentRegionInToursRegion = LUX_PLUS_TOURS_CAMPAIGN.TOUR_REGIONS.has(currentRegionCodeLower)
  const isCurrentMemberInHomeRegion =
    canRedeemLuxPlusBenefits && isMember && (memberRegion === currentRegionCodeLower)

  const now = new Date()
  const isCampaignActive =
    dateIsAfterOrSame(now, LUX_PLUS_TOURS_CAMPAIGN.START_DATE) &&
    dateIsBefore(now, LUX_PLUS_TOURS_CAMPAIGN.END_DATE)

  const [isDismissed, setIsDismissed] = useState(false)
  useEffect(() => {
    setIsDismissed(isBannerDismissed(BANNER_ID))
  }, [])
  const onDismiss = useCallback(() => {
    setIsDismissed(true)
    recordSiteBannerDismissal(BANNER_ID, LUX_PLUS_TOURS_CAMPAIGN.END_DATE.toISOString())
  }, [])

  const intl = useIntl()
  const [, handleSwitchRegion] = useSwitchRegion(memberRegion)

  let ctaLabel: string = ''
  let ctaTo: string | undefined
  let ctaHref: string | undefined
  let ctaOnClick: (() => void) | undefined
  let shouldShowForAudience = true

  if (isCurrentMemberInHomeRegion && isMembershipInToursRegion) {
    // Branch 1: active member in tours region, browsing own region
    ctaLabel = intl.formatMessage({
      id: '5QPg4DZI2K',
      defaultMessage: 'View details',
      description: 'CTA button label for LuxPlus+ tours banner when member is in their home region',
    })
    ctaTo = '/tours'
  } else if (isMembershipInToursRegion && currentRegionCodeLower !== memberRegion) {
    // Branch 2: member in tours region, browsing different region
    ctaLabel = intl.formatMessage({
      id: 'zYxPsv/RvN',
      defaultMessage: 'Switch to home region',
      description: 'CTA button label for LuxPlus+ tours banner when member is in a different region',
    })
    ctaHref = `/${memberRegion}/tours`
    ctaOnClick = handleSwitchRegion
  } else if (isMember && !isMembershipInToursRegion) {
    // Branch 3: member NOT in tours region -> suppress banner
    shouldShowForAudience = false
  } else if (!isMember && isCurrentRegionInToursRegion) {
    // Branch 4: non-member in tours region
    ctaLabel = intl.formatMessage({
      id: 'XKGNfoNFSw',
      defaultMessage: 'Become a member',
      description: 'CTA button label for LuxPlus+ tours banner for non-members',
    })
    ctaTo = '/lux-plus?luxplusExclusiveOffer=true'
  } else {
    // Branch 5: non-member NOT in tours region -> suppress banner
    shouldShowForAudience = false
  }

  const showBanner = isCampaignActive &&
    !isDismissed &&
    !isHidden &&
    !isCurrentRegionAustralia &&
    shouldShowForAudience

  return useMemo(
    () => ({
      showBanner,
      onDismiss,
      ctaLabel,
      ctaTo,
      ctaHref,
      ctaOnClick,
    }),
    [ctaHref, ctaLabel, ctaOnClick, ctaTo, onDismiss, showBanner],
  )
}

export default useLuxPlusToursOfferBanner
